import { ref, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import apiClient from '@/services/api'

const HEARTBEAT_INTERVAL_MS = 30_000 // 30 Sekunden

/**
 * Composable das alle 30 Sekunden einen Heartbeat an die API sendet.
 * Übermittelt die aktuelle Route und (falls erkennbar) die aktuelle Tool-ID.
 * Wird nur aktiviert, wenn der Benutzer authentifiziert ist.
 */
export function useHeartbeat() {
  const route = useRoute()
  const authStore = useAuthStore()
  const intervalId = ref<ReturnType<typeof setInterval> | null>(null)

  function extractToolPath(): string | null {
    const match = route.path.match(/^\/tools\/([^/]+)/)
    return match ? match[1] : null
  }

  async function sendHeartbeat() {
    if (!authStore.isAuthenticated) return
    try {
      await apiClient.post('/v1/activity/heartbeat', {
        current_route: route.path,
        // current_tool_id wird serverseitig aus der Route aufgelöst
      })
    } catch {
      // Heartbeat-Fehler nicht propagieren – stört UX nicht
    }
  }

  function start() {
    if (intervalId.value) return
    // Sofort ersten Heartbeat senden
    sendHeartbeat()
    intervalId.value = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS)
  }

  function stop() {
    if (intervalId.value) {
      clearInterval(intervalId.value)
      intervalId.value = null
    }
  }

  // Starten/stoppen je nach Auth-Status
  watch(
    () => authStore.isAuthenticated,
    (authenticated) => {
      if (authenticated) {
        start()
      } else {
        stop()
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    stop()
  })

  return { sendHeartbeat, start, stop }
}
